import React, { useRef, useEffect } from 'react';
import { observer } from 'mobx-react';
import './SOCrossslope.scss';
import { SOCrossslopeVM } from './SOCrossslopeVM';
import { D3LinePlot } from '../../common/D3LinePlot/D3LinePlot';
import { Table } from 'reactstrap';
import stores from 'cd-eft/models/Stores';
import { SOSurveyActions } from '../SOSurveyActions';
import { ButtonToPlaceRow } from '../SOSurveys';
import { Accordion } from 'cd-eft/components/common/Accordion';

const setStore = stores.AppStore.surveyEngineeringStore;
const SOCrossslopeComponent = (props) => {
  const vm = useRef(new SOCrossslopeVM(props)).current;

  const firstCrossSlopeStation = setStore.stationOffsetObject.crossSlopeStations[0];

  useEffect(() => {
    if (firstCrossSlopeStation) {
      vm.refillPlotData(firstCrossSlopeStation);
    }
  }, []);

  return (
    <>
      <Accordion headers={['Profile']} expanded={true} >
        <div className="so-graph-container">
          <D3LinePlot targetDivID={vm.targetDivID} windowOptions={vm.windowOptions} plotData={vm.plotData} />
        </div>
      </Accordion>

      <form className="usa-form usa-form--large form-with-fieldset">
        <fieldset className="usa-fieldset border-0 p-2">
          <div className="form-row-fixed-height mt-3">
            <label className="grid-col-2 usa-label" htmlFor="alignmentName">
              Alignment Name
            </label>
            <input
              className="grid-col-6 usa-input"
              name="alignmentName"
              aria-label="alignmentName"
              data-testid="alignmentName"
              placeholder={setStore.stationOffsetObject.alignmentName}
              value={setStore.stationOffsetObject.alignmentName}
              type="text"
              disabled
            />
          </div>
        </fieldset>

        <fieldset className="usa-fieldset p-2">
          <legend className="usa-legend usa-legend--small my-0">
            <div className="h5">Cross Slope</div>
          </legend>
          <div className="form-row-fixed-height">
            <label className="grid-col-2 usa-label" htmlFor="entryMode">
              Entry Mode
            </label>
            <select
              className="grid-col-3 usa-select"
              name="entryMode"
              aria-label="entryMode"
              data-testid="entryMode"
              value={firstCrossSlopeStation.entryMode}
              onChange={vm.setEntryMode}
            >
              <option value="">- Select -</option>
              <option value="PERCENTSLOPE">Percent Slope</option>
              <option value="SINGLESHOT">Single Shot</option>
            </select>
            {firstCrossSlopeStation.entryMode === 'PERCENTSLOPE' ? (
              <>
                <label className="grid-col-2 usa-label flex-justify-end">Uphill is to</label>
                <div className="grid-col-2 usa-radio">
                  <input
                    className="usa-radio__input"
                    id="left-table"
                    type="radio"
                    name="left-table"
                    value="LEFT"
                    checked={firstCrossSlopeStation.uphillsTo === 'LEFT'}
                    onChange={vm.setUphills}
                  />
                  <label className="usa-radio__label" htmlFor="left-table">
                    Left
                  </label>
                </div>

                <div className="grid-col-3 usa-radio">
                  <input
                    className="usa-radio__input"
                    id="right-table"
                    type="radio"
                    name="right-table"
                    value="RIGHT"
                    checked={firstCrossSlopeStation.uphillsTo === 'RIGHT'}
                    onChange={vm.setUphills}
                  />
                  <label className="usa-radio__label" htmlFor="right-table">
                    Right
                  </label>
                </div>
              </>
            ) : (
              <>
                <label className="grid-col-2 usa-label flex-justify-end" htmlFor="singleShotOffset">
                  Offset
                </label>
                {/* negative or positive if SINGLESHOT selected */}
                <input
                  className="grid-col-1 usa-input m-0"
                  name="singleShotOffset"
                  data-testid="singleShotOffset"
                  type="number"
                  value={firstCrossSlopeStation.singleShotOffset}
                  onChange={vm.updateAllcrossSlopeStations}
                />
              </>
            )}
          </div>
        </fieldset>

        <fieldset className="usa-fieldset p-2">
          <legend className="usa-legend usa-legend--small my-0">
            <div className="h5">Slope Width</div>
          </legend>
          <div className="form-row-fixed-height">
            <label className="grid-col-3 usa-label pr-3" htmlFor="slopeWidth">
              Left Limit (ft)
            </label>
            <input
              className="usa-input grid-col-1"
              name="slopeWidth"
              aria-label="slopeWidth"
              type="number"
              min={0}
              value={+firstCrossSlopeStation.slopeWidth}
              onChange={vm.updateAllcrossSlopeStations}
            />
            <label className="grid-col-3 usa-label flex-justify-end" htmlFor="right-limit">
              Right Limit (ft)
            </label>
            <input
              className="grid-col-1 usa-input"
              name="right-limit"
              aria-label="right-limit"
              type="number"
              min={0}
              value={+firstCrossSlopeStation.slopeWidth}
              disabled
            />
          </div>

          <div className="form-row-fixed-height mt-3">
            <label className="grid-col-3 usa-label nobr" htmlFor="stationStep">
              Station Step (ft)
            </label>
            <input
              className="usa-input grid-col-1"
              name="stationStep"
              aria-label="stationStep"
              type="number"
              min={0}
              value={+firstCrossSlopeStation.stationStep}
              onChange={vm.updateAllcrossSlopeStations}
            />

            <label className="grid-col-3 usa-label flex-justify-end">Entry Mode</label>

            <div className="grid-col-2 usa-radio">
              <input
                className="usa-radio__input"
                id="elevation"
                type="radio"
                name="elevation"
                aria-label="elevation"
                value="TYPE_ELEVATION"
                checked={firstCrossSlopeStation.offsetType === 'TYPE_ELEVATION'}
                onChange={vm.setType}
                disabled={
                  setStore.stationOffsetObject.surveyId !== '' && firstCrossSlopeStation.offsetType !== 'TYPE_ELEVATION'
                }
              />
              <label className="usa-radio__label" htmlFor="elevation">
                Elevation
              </label>
            </div>
            <div className="grid-col-3 usa-radio">
              <input
                className="usa-radio__input"
                id="rod"
                type="radio"
                name="rod"
                aria-label="rod"
                value="TYPE_ROD"
                checked={firstCrossSlopeStation.offsetType === 'TYPE_ROD'}
                onChange={vm.setType}
                disabled={
                  setStore.stationOffsetObject.surveyId !== '' && firstCrossSlopeStation.offsetType !== 'TYPE_ROD'
                }
              />
              <label className="usa-radio__label" htmlFor="rod">
                Rod
              </label>
            </div>
          </div>
        </fieldset>

        <Accordion headers={['Table']} expanded={true} >
          <div className="set-table-container height-auto">
            <Table className="set-table">
              <thead>
                <tr>
                  <th>Select</th>
                  <th>Station</th>
                  {firstCrossSlopeStation.offsetType === 'TYPE_ELEVATION' ? null : <th>HI</th>}
                  <th>0</th>
                  <th>
                    {firstCrossSlopeStation.entryMode === 'PERCENTSLOPE'
                      ? '% Slope'
                      : firstCrossSlopeStation.singleShotOffset}
                  </th>
                </tr>
              </thead>

              <tbody>
                {setStore.stationOffsetObject.crossSlopeStations.map((crossSlopeStation, i) => (
                  <tr key={`crossSlopeStation${i}`}>
                    <td className="text-center">
                      <input
                        id={'select' + i}
                        name="select"
                        aria-label={'select' + i}
                        data-testid={'select' + i}
                        type="checkbox"
                        value={i}
                        checked={vm.checkboxes.includes(i)}
                        onChange={vm.toggleCheckbox}
                      />
                      <ButtonToPlaceRow i={i} addRow={vm.addRow} />
                    </td>
                    <td width={firstCrossSlopeStation.offsetType === 'TYPE_ELEVATION' ? '33%' : '25%'}>
                      <input
                        className="usa-input"
                        name="stationDistance"
                        aria-label={`stationDistance${i}`}
                        data-testid={`stationDistance${i}`}
                        data-i={i}
                        type="number"
                        placeholder="0.00"
                        min={0}
                        onChange={vm.updateDistanceHandler}
                        value={crossSlopeStation.stationDistance}
                        onFocus={vm.onFocusHandler}
                        onBlur={vm.onBlurHandler}
                      />
                    </td>
                    {firstCrossSlopeStation.offsetType === 'TYPE_ROD' && (
                      <td width="25%">
                        <input
                          className="usa-input"
                          name="hiElevation"
                          aria-label={`hiElevation-${i}`}
                          data-testid={`hiElevation-${i}`}
                          data-i={i}
                          type="number"
                          placeholder="0.00"
                          min={0}
                          onChange={vm.updateHiElevation}
                          value={crossSlopeStation.hiElevation}
                          onFocus={vm.onFocusHandler}
                          onBlur={vm.onBlurHandler}
                        />
                      </td>
                    )}

                    {crossSlopeStation.crossSlopeStationOffsets.map((cs, j) => (
                      <React.Fragment key={`key-cs-${i}${j}`}>
                        <td
                          data-testid={`td-z${i}${j}`}
                          width={firstCrossSlopeStation.offsetType === 'TYPE_ELEVATION' ? '33%' : '25%'}
                        >
                          {/* negative or positive if ROD selected */}
                          <input
                            className="usa-input"
                            name="z"
                            aria-label={`z-${i}-${j}`}
                            data-testid={`z-${i}-${j}`}
                            data-i={i}
                            data-j={j}
                            type="number"
                            placeholder="0.00"
                            onChange={vm.updatecrossSlopeStationOffsetZ}
                            value={cs.z}
                            onFocus={vm.onFocusHandler}
                            onBlur={vm.onBlurHandler}
                          />
                        </td>
                        {firstCrossSlopeStation.entryMode === 'PERCENTSLOPE' && (
                          <td>
                            <input
                              className="usa-input"
                              name="crossSlopeStationSlope"
                              aria-label={`crossSlopeStationSlope${i}${j}`}
                              data-testid={`crossSlopeStationSlope${i}${j}`}
                              data-i={i}
                              data-j={j}
                              type="number"
                              placeholder="0.00"
                              min={0}
                              onChange={vm.updateCrossSlopeStationSlope}
                              value={cs.crossSlopeStationSlope}
                              onFocus={vm.onFocusHandler}
                              onBlur={vm.onBlurHandler}
                            />
                          </td>
                        )}
                      </React.Fragment>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
            <div className="container-for-last-element">
              <ButtonToPlaceRow i={setStore.stationOffsetObject.crossSlopeStations.length} addRow={vm.addRow} />
            </div>
          </div>
        </Accordion>

        <SOSurveyActions add={vm.addCSStationHandler} deleteStation={vm.deleteCSStationHandler} />
      </form>
    </>
  );
};

export const SOCrossslope = observer(SOCrossslopeComponent);
